import { authorizedFetch } from "../../api/http";
import type { JobAnalyticsEventName, JobAnalyticsRow } from "./tmHubReport.types";

const KNOWN_EVENTS: JobAnalyticsEventName[] = [
    "VIEW_POSITION",
    "FAVORITE",
    "COMPARE_POSITION",
];

function isEventName(s: string): s is JobAnalyticsEventName {
    return (KNOWN_EVENTS as readonly string[]).includes(s);
}

type ApiEvent = {
    id: number;
    user_id: string;
    event_name: string;
    current_pos: string | null;
    current_pos_id: string | null;
    source_pos: string | null;
    source_pos_id: string | null;
    target_pos: string | null;
    target_pos_id: string | null;
    app_version: string | null;
    metadata: unknown;
    created_at: string;
};

function mapApiEventToRow(raw: ApiEvent): JobAnalyticsRow | null {
    if (!isEventName(raw.event_name)) return null;
    return {
        id: raw.id,
        userId: String(raw.user_id),
        eventName: raw.event_name,
        currentPos: raw.current_pos,
        sourcePos: raw.source_pos,
        targetPos: raw.target_pos,
        currentPosId: raw.current_pos_id != null ? String(raw.current_pos_id) : null,
        sourcePosId: raw.source_pos_id != null ? String(raw.source_pos_id) : null,
        targetPosId: raw.target_pos_id != null ? String(raw.target_pos_id) : null,
        appVersion: raw.app_version,
        metadata: raw.metadata,
        createdAt: raw.created_at,
    };
}

export class JobAnalyticsFetchError extends Error {
    constructor(
        message: string,
        public readonly status?: number,
        public readonly cause?: unknown
    ) {
        super(message);
        this.name = "JobAnalyticsFetchError";
    }
}

export async function fetchJobAnalyticsEvents(
    startDate: string,
    endDate: string,
    signal?: AbortSignal
): Promise<JobAnalyticsRow[]> {
    const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "");
    if (!base) {
        throw new JobAnalyticsFetchError(
            "Missing REACT_APP_API_URL. Add it to your .env file."
        );
    }

    const params = new URLSearchParams({ startDate, endDate });
    const url = `${base}/job-analytics/events?${params.toString()}`;

    let response: Response;
    try {
        response = await authorizedFetch(url, {
            signal,
            method: "GET",
        });
    } catch (e) {
        if (e instanceof DOMException && e.name === "AbortError") throw e;
        throw new JobAnalyticsFetchError(
            e instanceof Error ? e.message : "Network request failed",
            undefined,
            e
        );
    }

    if (!response.ok) {
        let detail = response.statusText || "Unknown error";
        try {
            const ct = response.headers.get("content-type");
            if (ct?.includes("application/json")) {
                const body = await response.json();
                if (typeof body?.message === "string") detail = body.message;
                else if (typeof body?.error === "string") detail = body.error;
            }
        } catch {

        }
        throw new JobAnalyticsFetchError(
            `Request failed (${response.status}): ${detail}`,
            response.status
        );
    }

    let json: unknown;
    try {
        json = await response.json();
    } catch {
        throw new JobAnalyticsFetchError("Invalid JSON response from server.");
    }

    const events = (json as { events?: unknown }).events;
    if (!Array.isArray(events)) {
        throw new JobAnalyticsFetchError("Invalid response: expected an events array.");
    }

    const rows: JobAnalyticsRow[] = [];
    for (const item of events) {
        const row = mapApiEventToRow(item as ApiEvent);
        if (row) rows.push(row);
    }

    return rows.sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
}
